import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  fetchSupervisao,
  fetchSupervisaoFinalizadas,
} from '../../../api/portalCotacoes/portalCotacoesApi';
import {
  COTACAO_STATUS_LABEL,
  CotacaoStatus,
  SupervisaoFinalizadaLinha,
  SupervisaoResposta,
} from '../../../interfaces/PortalCotacoes';
import { AUDITORIA } from '../../auditoria/routes';
import SearchIcon from '../../../img/search-icon.svg';
import { CotacaoBadge } from '../components/PortalBadges';
import CotacaoFinalizadaModal from '../components/CotacaoFinalizadaModal';
import Paginacao from '../../../components/paginacao/Paginacao';
import { useTableColumns } from '../../../hooks/useTableColumns';
import RefreshIcon from '../../../img/refresh.svg';
import ComoFunciona, { ComoFuncionaSecao } from '../../../components/como-funciona/ComoFunciona';
import { PORTAL_COTACAO_DETALHE, PORTAL_COTACOES } from '../routes';
import ArrowLeftOrange from '../../../img/arrow-left-orange.svg';
import '../../../components/como-funciona/help-circle-btn.css';
import '../../../shared/feedback/erro-de-tela.css';
/*
 * Recepcao.css traz o esqueleto de página (`ConteDo`, `recepcao-title`, `CtaBig`) **e a
 * grade de tabela da Oficina** (`TabelaScroll`, `Tabela`, `Item`, `Data`, `PaginaO`), que
 * é o que as duas tabelas desta tela usam desde 14/08/2026 — a pedido do Victor, no molde
 * da tela de Usuários. PortalCotacaoDetalhe.css segue necessário pelos `pc-card` dos
 * cartões de resumo e dos estados vazios.
 *
 * Os imports são explícitos mesmo já estando no bundle pelas telas irmãs: depender do
 * import alheio quebraria esta tela se aquelas mudassem — foi assim que a primeira versão
 * desta página nasceu destoando das demais.
 */
import '../../recepcao/pages/Recepcao.css';
import './PortalCotacoes.css';
import './PortalCotacaoDetalhe.css';
// Selo de status compartilhado (at-11: o "Parada" da coluna de dias na etapa). Importado
// explicitamente, nunca herdado do bundle de outra tela — é a armadilha do BUG-V74.
import '../../../shared/ui/badge-status.css';

/**
 * Supervisão do fluxo (13/08/2026 — item 9 do backlog do módulo).
 *
 * Fecha o terceiro objetivo declarado pelo grupo na reunião de 21/07/2026 (padronizar
 * valores/prazos, auditoria e **supervisão**), o único que nunca havia virado tela.
 *
 * ⚠️ **Não é fila de trabalho.** A fila do gestor é a tela de Aprovações; aqui ninguém decide
 * nada. O objetivo é enxergar o que está PARADO — por isso a ordenação (feita no backend) é
 * por tempo na etapa atual, do mais parado para o menos.
 */

const helpSections: ComoFuncionaSecao[] = [
  {
    titulo: 'Para que serve esta tela',
    conteudo: (
      <div>
        <p>
          É uma visão de <strong>acompanhamento</strong>, não uma fila de trabalho: nada é aprovado ou decidido
          aqui. Para decidir sobre item acima do menor preço, a tela é <strong>Aprovações</strong>.
        </p>
        <p>
          São duas leituras: <strong>Em andamento</strong> responde "o que está parado?" — as cotações
          aparecem da mais parada para a menos; <strong>Finalizadas</strong> responde "o que aconteceu?" nas
          que já terminaram.
        </p>
      </div>
    ),
  },
  {
    titulo: 'Como ler "dias na etapa"',
    conteudo: (
      <div>
        <p>
          É o tempo desde que a cotação <strong>entrou na etapa em que está agora</strong>, não desde que foi
          criada. Uma cotação criada há 10 dias e enviada ontem aparece com 1 dia: o que interessa é há quanto
          tempo ela está esperando <em>naquilo</em>.
        </p>
        <p>
          O marco muda conforme a etapa: rascunho conta da criação; enviada e em resposta contam do envio;
          decisão conta do encerramento das respostas; aprovação conta do fechamento da cesta; e a geração de
          pedidos conta de quando a cesta ficou liberada. <strong>Reprovação do gestor reinicia a contagem da
          decisão</strong> — o comprador precisa redecidir, e cobrar dele o tempo que a cotação passou com o
          gestor seria injusto.
        </p>
        <p>
          O selo <strong>Parada</strong> aparece quando a etapa passou do tempo esperado, configurado em
          Administração do Sistema → Cotações → Alertas. É a <strong>mesma régua</strong> do aviso que chega ao
          sino, então os dois nunca discordam. Etapa sem prazo configurado não recebe selo — e as etapas de
          resposta seguem o prazo da própria cotação, que é a coluna ao lado.
        </p>
      </div>
    ),
  },
  {
    titulo: 'Prazo vencido e tempos médios',
    conteudo: (
      <div>
        <p>
          <strong>Prazo vencido</strong> só é destacado enquanto a cotação ainda espera resposta. Depois do
          encerramento, o prazo passado é o normal — marcar aquilo como atraso seria alarme falso.
        </p>
        <p>
          Os <strong>tempos médios</strong> cobrem apenas as cotações que já percorreram cada trecho. Quando
          ainda não há nenhuma, o valor aparece como <strong>—</strong>: significa "sem amostra", que é
          diferente de zero.
        </p>
      </div>
    ),
  },
  {
    titulo: 'Aba Finalizadas: o que aconteceu',
    conteudo: (
      <div>
        <p>
          Reúne as cotações que chegaram ao fim, tanto as <strong>concluídas</strong> (viraram pedido no ERP)
          quanto as <strong>canceladas</strong> — com o motivo do cancelamento ao lado do desfecho.
        </p>
        <p>
          A tabela mostra o essencial; o botão <strong>Detalhes</strong>, na coluna Ações, abre o resto:
          comprador, quantos fornecedores foram convidados, datas de início e fim, o que o gestor decidiu e
          quem participou.
        </p>
        <p>
          <strong>Aprovação do gestor</strong> conta tudo o que ele decidiu ao longo da cotação, e não só a
          última palavra: um item pode ter sido reprovado, revisto pelo comprador e aprovado depois. Quando
          aparece <strong>Não precisou</strong>, é porque todos os itens saíram no menor preço e o gestor
          nunca foi acionado — diferente de ter sido acionado e não ter decidido.
        </p>
        <p>
          <strong>Participaram</strong> lista quem aparece no histórico da cotação. Cotação anterior a essa
          gravação aparece sem nenhum nome: significa que não foi registrado à época, não que ninguém
          participou.
        </p>
      </div>
    ),
  },
  {
    titulo: 'Ver histórico: o passo a passo fica na Auditoria',
    conteudo: (
      <p>
        O botão <strong>Ver histórico</strong> abre a <strong>Auditoria</strong> já com a cotação escolhida.
        É lá que está tudo o que foi feito, em ordem: convites e disparos, aberturas do link pelo fornecedor,
        propostas recebidas, escolhas do comprador, aprovações e reprovações do gestor, fechamento da cesta e
        os pedidos gerados. Esta tela resume o desfecho; a Auditoria conta a história.
      </p>
    ),
  },
];

function media(valor: number | null): string {
  // "—" e não 0: zero afirmaria "leva menos de um dia", que é outra coisa.
  return valor === null ? '—' : `${valor.toLocaleString('pt-BR')} d`;
}

function formatarData(iso: string): string {
  return new Date(iso).toLocaleDateString('pt-BR');
}

/**
 * O que o gestor fez na cotação, em uma frase.
 *
 * `null` (nunca precisou) é DIFERENTE de zero decisões: a primeira diz que tudo saiu no
 * menor preço e ninguém foi acionado; a segunda diria que foi acionado e nada respondeu.
 * Misturar as duas é o tipo de erro que só aparece quando alguém pergunta "e a aprovação?".
 */
function resumoDoGestor(gestor: SupervisaoFinalizadaLinha['gestor']): string {
  if (!gestor) return 'Não precisou';
  const { aprovadas, reprovadas } = gestor;
  if (aprovadas === 0 && reprovadas === 0) return 'Solicitada, sem decisão';
  const partes: string[] = [];
  if (aprovadas > 0) partes.push(`${aprovadas} ${aprovadas === 1 ? 'aprovação' : 'aprovações'}`);
  if (reprovadas > 0) partes.push(`${reprovadas} ${reprovadas === 1 ? 'reprovação' : 'reprovações'}`);
  return partes.join(' · ');
}

const PAGE_SIZE_FINALIZADAS = 10;

/*
 * Cabeçalhos das duas tabelas. Ficam fora do componente porque alimentam a medição de
 * colunas (`useTableColumns`) — array recriado a cada render derrubaria o ResizeObserver.
 */
const HEADERS_ANDAMENTO = [
  'Cotação',
  'Veículo',
  'Comprador',
  'Status',
  'Respostas',
  'Dias na etapa',
  'Prazo',
];

/*
 * Cinco colunas, por decisão do Victor (14/08/2026): a tabela mostra o que se lê de
 * relance, e comprador, aprovação do gestor, participantes e datas foram para o modal de
 * "Detalhes". Tabela larga obriga a rolar na horizontal para ler uma linha inteira — o
 * oposto do que uma visão de acompanhamento precisa.
 */
const HEADERS_FINALIZADAS = ['Cotação', 'Veículo', 'Desfecho', 'Histórico', 'Ações'];

const LARGURA_MINIMA_COLUNA = 140;

const PortalCotacoesSupervisao: React.FC = () => {
  const navigate = useNavigate();
  const [dados, setDados] = useState<SupervisaoResposta | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [ajudaAberta, setAjudaAberta] = useState(false);

  // ── Aba "Finalizadas" (14/08/2026) ────────────────────────────────────────
  const [aba, setAba] = useState<'andamento' | 'finalizadas'>('andamento');
  /* `null` = não carregou (ou falhou). Lista vazia é afirmação: "não há finalizadas". */
  const [finalizadas, setFinalizadas] = useState<SupervisaoFinalizadaLinha[] | null>(null);
  const [totalFinalizadas, setTotalFinalizadas] = useState(0);
  const [paginaFinalizadas, setPaginaFinalizadas] = useState(1);
  const [buscaInput, setBuscaInput] = useState('');
  const [buscaAplicada, setBuscaAplicada] = useState('');
  const [carregandoFinalizadas, setCarregandoFinalizadas] = useState(false);
  const [erroFinalizadas, setErroFinalizadas] = useState<string | null>(null);

  /*
   * Cotação aberta no modal de detalhes. Guarda a LINHA inteira (não só o id): os dados já
   * vieram na listagem, e buscar de novo por id criaria uma segunda fonte para o mesmo
   * dado — com a chance de o modal mostrar um número diferente do da linha clicada.
   */
  const [cotacaoDetalhada, setCotacaoDetalhada] = useState<SupervisaoFinalizadaLinha | null>(null);

  // Uma tabela por aba, cada uma com o próprio container e cabeçalhos para a medição.
  const scrollAndamentoRef = useRef<HTMLDivElement>(null);
  const headersAndamentoRefs = useRef<(HTMLElement | null)[]>([]);
  const scrollFinalizadasRef = useRef<HTMLDivElement>(null);
  const headersFinalizadasRefs = useRef<(HTMLElement | null)[]>([]);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      setDados(await fetchSupervisao());
      setErro(null);
    } catch (error) {
      /*
       * A faixa CONVIVE com o que já está na tela (regra da at-65): montar o conteúdo sob
       * `!erro` apagaria cabeçalho e ajuda de uma vez. E o estado fica nulo em vez de virar
       * lista vazia — "nenhuma cotação em curso" é conclusão de negócio, e só pode ser dita
       * quando a consulta respondeu.
       */
      setErro((error as Error).message);
      setDados(null);
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    if (aba === 'andamento') carregar();
  }, [carregar, aba]);

  /*
   * Finalizadas carregam SÓ quando a aba é aberta, e por página: a lista de terminadas só
   * cresce (nada sai dela), enquanto as em curso são poucas por natureza. Carregar as duas
   * de uma vez faria toda visita à tela pagar por um histórico que ninguém pediu.
   */
  const carregarFinalizadas = useCallback(async () => {
    setCarregandoFinalizadas(true);
    try {
      const resposta = await fetchSupervisaoFinalizadas(buscaAplicada, paginaFinalizadas, PAGE_SIZE_FINALIZADAS);
      setFinalizadas(resposta.data);
      setTotalFinalizadas(resposta.total);
      setErroFinalizadas(null);
    } catch (error) {
      // Mesmo princípio da aba em curso: erro não vira lista vazia (que se lê como
      // "nenhuma cotação finalizada" — conclusão de negócio que a consulta não sustenta).
      setErroFinalizadas((error as Error).message);
      setFinalizadas(null);
      setTotalFinalizadas(0);
    } finally {
      setCarregandoFinalizadas(false);
    }
  }, [buscaAplicada, paginaFinalizadas]);

  useEffect(() => {
    if (aba === 'finalizadas') carregarFinalizadas();
  }, [carregarFinalizadas, aba]);

  // Busca com espera: cada tecla digitada não vira uma requisição.
  useEffect(() => {
    const timer = setTimeout(() => {
      setBuscaAplicada(buscaInput.trim());
      setPaginaFinalizadas(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [buscaInput]);

  /*
   * `useMemo` e não `dados?.emCurso ?? []` solto: o array novo a cada render entraria nas
   * dependências da medição de colunas e faria o `ResizeObserver` cair e subir sem parar
   * (é o que o `react-hooks/exhaustive-deps` acusa, e o motivo de o hook exigir `rows`
   * memoizado).
   */
  const emCurso = useMemo(() => dados?.emCurso ?? [], [dados]);

  /*
   * Medição das colunas pelo hook compartilhado (at-43), o mesmo que as tabelas da Oficina
   * usam: a largura de cada coluna vem do conteúdo real, medido com `canvas.measureText`.
   * `enabled` desliga a medição da aba oculta (o container nem está montado) e
   * `remeasureKey` religa o ResizeObserver no container novo quando a aba troca.
   */
  const linhasAndamento = useMemo(
    () =>
      emCurso.map((l) => [
        l.numero,
        `${l.veiculo ?? '—'} ${l.placa ?? ''}`.trim(),
        l.compradorNome ?? '—',
        COTACAO_STATUS_LABEL[l.status],
        `${l.respostas.responderam}/${l.respostas.convidados}`,
        `${l.diasNaEtapaAtual} ${l.diasNaEtapaAtual === 1 ? 'dia' : 'dias'}`,
        `${formatarData(l.prazoRespostaAte)}${l.prazoVencido ? ' vencido' : ''}`,
      ]),
    [emCurso],
  );

  const { tableColumns: colunasAndamento, tableWidth: larguraAndamento } = useTableColumns({
    containerRef: scrollAndamentoRef,
    headerCellsRef: headersAndamentoRefs,
    headers: HEADERS_ANDAMENTO,
    defaultWidths: HEADERS_ANDAMENTO.map(() => LARGURA_MINIMA_COLUNA),
    rows: linhasAndamento,
    enabled: aba === 'andamento',
    remeasureKey: aba,
    cellSelector: '.TabelaContent > .Tabela:not(.TabelaHeader) .Data',
  });

  const linhasFinalizadas = useMemo(
    () =>
      (finalizadas ?? []).map((l) => [
        l.numero,
        `${l.veiculo ?? '—'} ${l.placa ?? ''}`.trim(),
        `${COTACAO_STATUS_LABEL[l.status]} ${l.cancelamentoMotivo ?? ''}`.trim(),
        'Ver histórico',
        'Detalhes',
      ]),
    [finalizadas],
  );

  const { tableColumns: colunasFinalizadas, tableWidth: larguraFinalizadas } = useTableColumns({
    containerRef: scrollFinalizadasRef,
    headerCellsRef: headersFinalizadasRefs,
    headers: HEADERS_FINALIZADAS,
    defaultWidths: HEADERS_FINALIZADAS.map(() => LARGURA_MINIMA_COLUNA),
    rows: linhasFinalizadas,
    enabled: aba === 'finalizadas',
    remeasureKey: aba,
    cellSelector: '.TabelaContent > .Tabela:not(.TabelaHeader) .Data',
  });

  const primeiroIndiceFinalizadas =
    totalFinalizadas === 0 ? 0 : (paginaFinalizadas - 1) * PAGE_SIZE_FINALIZADAS + 1;
  const ultimoIndiceFinalizadas = Math.min(paginaFinalizadas * PAGE_SIZE_FINALIZADAS, totalFinalizadas);
  const porStatus = dados?.resumo.porStatus ?? {};
  const tempos = dados?.resumo.temposMediosDias;

  /*
   * Esqueleto de página IGUAL ao da lista e do detalhe (13/08/2026 — pedido do Victor: a
   * tela destoava em margens, fontes e posicionamento). O visual das páginas do Portal não
   * vem do `pc-header`: vem do esqueleto da Oficina — `Frame1000004012` + `ConteDo
   * recepcao-conte-do` (largura e margens do conteúdo) + `recepcao-title` (o título grande
   * em AmpleSoft) — com o botão branco de ação no topo direito. A primeira versão desta tela
   * usou só as classes `pc-*` e ficou com título miúdo e conteúdo colado nas bordas.
   */
  return (
    <div
      className="Frame1000004012"
      style={{
        height: 'fit-content',
        width: '100%',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        backgroundColor: 'transparent',
      }}
    >
      <div className="ConteDo recepcao-conte-do pc-page">
        <div
          className="recepcao-header-row"
          style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}
        >
          <div>
            <div className="recepcao-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              Supervisão
              <button
                type="button"
                className="help-circle-btn"
                title="Como Funciona"
                onClick={() => setAjudaAberta(true)}
              >
                ?
              </button>
            </div>
            <p className="pc-header-sub" style={{ margin: '4px 0 0', fontSize: '13px', color: '#6b7a99' }}>
              {aba === 'andamento'
                ? 'Cotações em andamento e tempo em cada etapa — as decisões ficam nas telas de cotação e de aprovação.'
                : 'Cotações encerradas e o que aconteceu em cada uma — o passo a passo completo fica na Auditoria.'}
            </p>
          </div>
          <div className="pc-header-acoes" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {/* Mesmo botão branco do "Voltar à lista" do detalhe — o padrão de ação do topo. */}
            <div
              className="CtaBig MouseHover"
              onClick={() => {
                if (aba === 'andamento') {
                  if (!carregando) carregar();
                } else if (!carregandoFinalizadas) {
                  carregarFinalizadas();
                }
              }}
              title={
                aba === 'andamento'
                  ? 'Recarregar as cotações em andamento'
                  : 'Recarregar as cotações finalizadas'
              }
              style={{
                padding: '7px 16px',
                backgroundColor: 'var(--tema-superficie, white)',
                border: '1px solid var(--tema-linha-2, #CACACA)',
                cursor: carregando || carregandoFinalizadas ? 'default' : 'pointer',
                opacity: carregando || carregandoFinalizadas ? 0.6 : 1,
                marginRight: 0,
              }}
            >
              <div className="Frame1000003987" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <img src={RefreshIcon} alt="" width="18" height="18" />
                <span style={{ color: 'var(--tema-texto, #1A1C1B)', fontWeight: 600, fontSize: '14px', fontFamily: 'AmpleSoft-Bold, sans-serif' }}>
                  Atualizar
                </span>
              </div>
            </div>
            <div
              className="CtaBig MouseHover"
              onClick={() => navigate(PORTAL_COTACOES())}
              style={{
                padding: '7px 16px',
                backgroundColor: 'var(--tema-superficie, white)',
                border: '1px solid var(--tema-linha-2, #CACACA)',
                cursor: 'pointer',
                marginRight: 0,
              }}
            >
              <div className="Frame1000003987" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <img src={ArrowLeftOrange} alt="" width="16" height="12" />
                <span style={{ color: 'var(--tema-texto, #1A1C1B)', fontWeight: 600, fontSize: '14px', fontFamily: 'AmpleSoft-Bold, sans-serif' }}>
                  Voltar à lista
                </span>
              </div>
            </div>
          </div>
        </div>

      {/*
        Duas leituras da mesma tela (14/08/2026, pedido do Victor): o que está correndo e o
        que já terminou. `role="tab"` + `aria-selected` para o teste distinguir a aba do
        resto do texto da página — mesmo padrão da Auditoria.
      */}
      <div className="recepcao-tabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={aba === 'andamento'}
          className={`recepcao-tab ${aba === 'andamento' ? 'active' : ''}`}
          onClick={() => setAba('andamento')}
        >
          Em andamento
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={aba === 'finalizadas'}
          className={`recepcao-tab ${aba === 'finalizadas' ? 'active' : ''}`}
          onClick={() => setAba('finalizadas')}
        >
          Finalizadas
        </button>
      </div>

      {aba === 'andamento' && (
        <>
      {erro && (
        <p className="erro-de-tela">
          Não foi possível carregar a supervisão: {erro} Recarregue a página; se persistir, acione a TI.
        </p>
      )}

      <div className="pc-supervisao-cards">
        <div className="pc-card pc-supervisao-card">
          <p className="pc-resumo-rotulo">Em andamento</p>
          <p className="pc-supervisao-numero">{dados ? emCurso.length : '—'}</p>
          <p className="pc-texto-secundario">
            {dados
              ? Object.entries(porStatus)
                  .map(([status, qtd]) => `${qtd} ${COTACAO_STATUS_LABEL[status as CotacaoStatus]}`)
                  .join(' · ') || 'Nenhuma cotação em andamento'
              : 'Sem dados carregados'}
          </p>
        </div>
        <div className="pc-card pc-supervisao-card">
          <p className="pc-resumo-rotulo">Tempos médios</p>
          <p className="pc-texto-secundario" style={{ margin: '4px 0' }}>
            Rascunho até envio: <strong>{media(tempos?.rascunhoAteEnvio ?? null)}</strong>
          </p>
          <p className="pc-texto-secundario" style={{ margin: '4px 0' }}>
            Envio até decisão: <strong>{media(tempos?.envioAteDecisao ?? null)}</strong>
          </p>
          <p className="pc-texto-secundario" style={{ margin: '4px 0' }}>
            Decisão até conclusão: <strong>{media(tempos?.decisaoAteConclusao ?? null)}</strong>
          </p>
        </div>
      </div>

      {carregando && <div className="pc-carregando">Carregando...</div>}

      {!carregando && dados && emCurso.length === 0 && (
        <div className="pc-card">
          <div className="pc-vazio">Nenhuma cotação em andamento.</div>
        </div>
      )}

      {emCurso.length > 0 && (
        <>
          {/*
            Contador acima da tabela, no mesmo aninhamento das telas da Oficina
            (`QuantidadeDeCarros` > `Frame1000004037` > `CtaQuantidadeDeCarros`): as classes
            de fora carregam o espaçamento, e só a de dentro tem tipografia.
          */}
          <div className="QuantidadeDeCarros">
            <div
              className="Frame1000004037"
              style={{ alignSelf: 'stretch', justifyContent: 'flex-start', alignItems: 'center', gap: 12, display: 'flex' }}
            >
              <div className="CtaQuantidadeDeCarros">
                <div className="Frame1000003987">
                  <div className="ComponentName">
                    {emCurso.length} {emCurso.length === 1 ? 'cotação em andamento' : 'cotações em andamento'}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="pc-tabela-oficina-card">
            <div className="TabelaScroll" ref={scrollAndamentoRef}>
              <div
                className="TabelaContent"
                style={{
                  ['--table-columns' as string]: colunasAndamento,
                  width: larguraAndamento,
                  minWidth: larguraAndamento,
                }}
              >
                <div className="Tabela TabelaHeader">
                  <div className="Placa">
                    <div className="TTulo">
                      {HEADERS_ANDAMENTO.map((titulo, i) => (
                        <div
                          key={titulo}
                          className="Opcoes"
                          ref={(el) => {
                            headersAndamentoRefs.current[i] = el;
                          }}
                        >
                          {titulo}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {emCurso.map((linha) => (
                  <div key={linha.id} className="Tabela">
                    {/*
                      Só o NÚMERO leva ao detalhe (13/08/2026, decisão do Victor). Antes a
                      linha inteira tinha o `onClick`, o que é alvo grande e sem contorno:
                      qualquer clique na linha navegava, inclusive um clique de leitura ou
                      uma seleção de texto arrastando pela célula. `<button>` em vez de
                      `<span>` para o alvo ser alcançável por teclado e anunciado como ação.
                    */}
                    <div className="Item">
                      <div className="Data">
                        <button
                          type="button"
                          className="pc-link-numero"
                          title={`Requisição ${linha.solicitacaoNumero}`}
                          onClick={() => navigate(PORTAL_COTACAO_DETALHE(linha.id))}
                        >
                          {linha.numero}
                        </button>
                      </div>
                    </div>
                    {/*
                      Célula de UMA linha (regra do Victor, 12/08/2026): veículo e placa lado
                      a lado, com reticências e o valor inteiro no `title`.
                    */}
                    <div className="Item">
                      <div className="Data">
                        <span className="pc-data-texto" title={`${linha.veiculo ?? ''} ${linha.placa ?? ''}`.trim()}>
                          {linha.veiculo ?? '—'}
                          {linha.placa && <span className="pc-celula-secundario">{linha.placa}</span>}
                        </span>
                      </div>
                    </div>
                    <div className="Item">
                      <div className="Data">
                        <span className="pc-data-texto" title={linha.compradorNome ?? ''}>
                          {linha.compradorNome ?? '—'}
                        </span>
                      </div>
                    </div>
                    <div className="Item">
                      <div className="Data">
                        <CotacaoBadge status={linha.status} />
                      </div>
                    </div>
                    <div className="Item">
                      <div className="Data">
                        {linha.respostas.responderam}/{linha.respostas.convidados}
                      </div>
                    </div>
                    <div className="Item">
                      <div className="Data">
                        {linha.diasNaEtapaAtual} {linha.diasNaEtapaAtual === 1 ? 'dia' : 'dias'}
                        {/*
                          Selo "Parada" (at-11): a mesma régua do aviso do sino — o limiar vem
                          pronto do backend (`limiarDias`), e a tela não recalcula nada. Com
                          duas réguas, a linha diria "parada" num dia diferente do aviso, sobre
                          a mesma cotação.
                          ⚠️ `limiarDias` nulo = etapa não vigiada: nenhum selo, nunca um
                          limiar inventado.
                        */}
                        {linha.limiarDias != null && linha.diasNaEtapaAtual >= linha.limiarDias && (
                          <span
                            className="cilia-badge badge-laranja"
                            style={{ marginLeft: 6 }}
                            title={`O esperado nesta etapa é ${linha.limiarDias} ${linha.limiarDias === 1 ? 'dia' : 'dias'}`}
                          >
                            Parada
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="Item">
                      <div className="Data">
                        <span
                          className={linha.prazoVencido ? 'pc-prazo-vencido' : undefined}
                          title={linha.prazoVencido ? 'Prazo de resposta vencido' : undefined}
                        >
                          {formatarData(linha.prazoRespostaAte)}
                          {linha.prazoVencido && <span className="pc-celula-secundario">vencido</span>}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
        </>
      )}

      {aba === 'finalizadas' && (
        <>
          {erroFinalizadas && (
            <p className="erro-de-tela">
              Não foi possível carregar as cotações finalizadas: {erroFinalizadas} Recarregue a página; se
              persistir, acione a TI.
            </p>
          )}

          <div className="recepcao-search-bar" style={{ marginBottom: 20 }}>
            <div className="ItemSearch">
              <img className="imgIcon" src={SearchIcon} alt="Ícone de busca" width="20" height="20" />
            </div>
            <input
              type="text"
              className="recepcao-search-input"
              placeholder="Pesquise por número, placa, veículo ou comprador"
              value={buscaInput}
              onChange={(e) => setBuscaInput(e.target.value)}
            />
          </div>

          {carregandoFinalizadas && <div className="pc-carregando">Carregando...</div>}

          {!carregandoFinalizadas && finalizadas && finalizadas.length === 0 && (
            <div className="pc-card">
              <div className="pc-vazio">
                {buscaAplicada
                  ? 'Nenhuma cotação finalizada corresponde à busca.'
                  : 'Nenhuma cotação finalizada ainda.'}
              </div>
            </div>
          )}

          {finalizadas && finalizadas.length > 0 && (
            <>
              {/* Contador acima da tabela, no padrão das telas da Oficina. */}
              <div className="QuantidadeDeCarros">
                <div
                  className="Frame1000004037"
                  style={{ alignSelf: 'stretch', justifyContent: 'flex-start', alignItems: 'center', gap: 12, display: 'flex' }}
                >
                  <div className="CtaQuantidadeDeCarros">
                    <div className="Frame1000003987">
                      <div className="ComponentName">
                        {primeiroIndiceFinalizadas}-{ultimoIndiceFinalizadas} de {totalFinalizadas}{' '}
                        {totalFinalizadas === 1 ? 'cotação finalizada' : 'cotações finalizadas'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="pc-tabela-oficina-card">
                <div className="TabelaScroll" ref={scrollFinalizadasRef}>
                  <div
                    className="TabelaContent"
                    style={{
                      ['--table-columns' as string]: colunasFinalizadas,
                      width: larguraFinalizadas,
                      minWidth: larguraFinalizadas,
                    }}
                  >
                    <div className="Tabela TabelaHeader">
                      <div className="Placa">
                        <div className="TTulo">
                          {HEADERS_FINALIZADAS.map((titulo, i) => (
                            <div
                              key={titulo}
                              className="Opcoes"
                              ref={(el) => {
                                headersFinalizadasRefs.current[i] = el;
                              }}
                            >
                              {titulo}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    {finalizadas.map((linha) => (
                      <div key={linha.id} className="Tabela">
                        <div className="Item">
                          <div className="Data">
                            <button
                              type="button"
                              className="pc-link-numero"
                              onClick={() => navigate(PORTAL_COTACAO_DETALHE(linha.id))}
                            >
                              {linha.numero}
                            </button>
                          </div>
                        </div>
                        {/* Célula de UMA linha (regra do Victor, 12/08/2026). */}
                        <div className="Item">
                          <div className="Data">
                            <span
                              className="pc-data-texto"
                              title={`${linha.veiculo ?? ''} ${linha.placa ?? ''}`.trim()}
                            >
                              {linha.veiculo ?? '—'}
                              {linha.placa && <span className="pc-celula-secundario">{linha.placa}</span>}
                            </span>
                          </div>
                        </div>
                        <div className="Item">
                          <div
                            className="Data"
                            title={
                              linha.status === 'CANCELADA' && linha.cancelamentoMotivo
                                ? `Motivo: ${linha.cancelamentoMotivo}`
                                : undefined
                            }
                          >
                            <CotacaoBadge status={linha.status} />
                            {/*
                              O motivo do cancelamento ao lado do badge: sem ele a linha
                              diz que a cotação morreu e não diz por quê, que é a primeira
                              pergunta de quem abre a aba.
                            */}
                            {linha.status === 'CANCELADA' && linha.cancelamentoMotivo && (
                              <span className="pc-celula-secundario pc-data-texto">
                                {linha.cancelamentoMotivo}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="Item">
                          <div className="Data">
                            {/*
                              Aponta para a Auditoria em vez de repetir o passo a passo aqui:
                              a timeline já funde as três fontes (ações internas, trilha do
                              fornecedor e disparos), e uma segunda versão da mesma história
                              divergiria na primeira mudança de regra.
                            */}
                            <button
                              type="button"
                              className="pc-link-numero"
                              title={`Ver o histórico completo da cotação ${linha.numero} na Auditoria`}
                              onClick={() =>
                                navigate(AUDITORIA(), {
                                  state: {
                                    cotacaoParaAuditar: {
                                      id: linha.id,
                                      numero: linha.numero,
                                      placa: linha.placa,
                                      veiculo: linha.veiculo,
                                      status: linha.status,
                                      createdAt: linha.criadaEm,
                                    },
                                  },
                                })
                              }
                            >
                              Ver histórico
                            </button>
                          </div>
                        </div>
                        <div className="Item">
                          <div className="Data">
                            {/*
                              O resto do que a linha mostrava até 14/08 (comprador, gestor,
                              participantes, datas) vive aqui dentro. `<button>` com rótulo
                              explícito, e não um ícone: a coluna "Ações" é o único caminho
                              para esses dados, então precisa se anunciar.
                            */}
                            <button
                              type="button"
                              className="pc-btn pc-btn--secundario"
                              title={`Ver os detalhes da cotação ${linha.numero}`}
                              onClick={() => setCotacaoDetalhada(linha)}
                            >
                              Detalhes
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/*
                  A paginação fica DENTRO do cartão branco, como na tela de Usuários — é o
                  rodapé da tabela, não um bloco solto abaixo dela.
                */}
                {totalFinalizadas > PAGE_SIZE_FINALIZADAS && (
                  <Paginacao
                    paginaAtual={paginaFinalizadas}
                    totalPaginas={Math.ceil(totalFinalizadas / PAGE_SIZE_FINALIZADAS)}
                    onMudarPagina={setPaginaFinalizadas}
                  />
                )}
              </div>
            </>
          )}
        </>
      )}

        {/*
          Montagem condicional (padrão da at-61): o estado do modal nasce do que foi
          clicado, sem efeito recopiando props a cada abertura.
        */}
        {cotacaoDetalhada && (
          <CotacaoFinalizadaModal
            cotacao={cotacaoDetalhada}
            resumoGestor={resumoDoGestor(cotacaoDetalhada.gestor)}
            onFechar={() => setCotacaoDetalhada(null)}
            onVerHistorico={() =>
              navigate(AUDITORIA(), {
                state: {
                  cotacaoParaAuditar: {
                    id: cotacaoDetalhada.id,
                    numero: cotacaoDetalhada.numero,
                    placa: cotacaoDetalhada.placa,
                    veiculo: cotacaoDetalhada.veiculo,
                    status: cotacaoDetalhada.status,
                    createdAt: cotacaoDetalhada.criadaEm,
                  },
                },
              })
            }
            onAbrirCotacao={() => navigate(PORTAL_COTACAO_DETALHE(cotacaoDetalhada.id))}
          />
        )}

        <ComoFunciona
          isOpen={ajudaAberta}
          onClose={() => setAjudaAberta(false)}
          titulo="Como Funciona - Supervisão"
          secoes={helpSections}
        />
      </div>
    </div>
  );
};

export default PortalCotacoesSupervisao;
